import React, { useState } from 'react';
import { Sparkles, TrendingUp, TrendingDown, Loader2, Check, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { rateApi } from '../lib/api';
import { toast } from '../hooks/use-toast';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function AiRateSuggest({ origin, destination, truckCode, material, weight, currentRate, onApply }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const suggest = async () => {
    if (!origin || !destination) {
      toast({ title: 'Select route first', description: 'Pick origin and destination to get an AI rate.' });
      return;
    }
    setLoading(true);
    try {
      const r = await rateApi.suggest({ origin, destination, truck_code: truckCode || 'TR-32', material: material || '', weight: weight || 10 });
      setSuggestion(r);
    } catch (err) {
      toast({ title: 'Could not fetch suggestion', description: 'Try again shortly' });
    } finally { setLoading(false); }
  };

  const apply = () => {
    if (suggestion) { onApply(suggestion.suggested_rate); toast({ title: 'Rate applied', description: `₹${suggestion.suggested_rate.toLocaleString('en-IN')} suggested by EZLOAD AI.` }); }
  };

  const rateVsSuggested = currentRate && suggestion ? ((parseInt(currentRate) - suggestion.suggested_rate) / suggestion.suggested_rate) * 100 : 0;

  if (!suggestion) {
    return (
      <Button type="button" onClick={suggest} disabled={loading || !origin || !destination} size="sm" variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
        AI Suggest Rate
      </Button>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#0B2545]">EZLOAD AI Rate</p>
            <p className="text-[10px] text-gray-600">{suggestion.confidence_pct}% confidence • {suggestion.historical_samples} similar loads</p>
          </div>
        </div>
        <Badge className="bg-purple-600 text-white border-0">SUGGESTED</Badge>
      </div>

      <div>
        <p className="text-3xl font-extrabold text-purple-700 font-[Poppins]">{formatINR(suggestion.suggested_rate)}</p>
        <p className="text-xs text-gray-600 mt-0.5">Fair range: {formatINR(suggestion.rate_range_low)} – {formatINR(suggestion.rate_range_high)} • ₹{suggestion.rate_per_km}/km • ~{suggestion.distance_km} km</p>
      </div>

      {currentRate && (
        <div className={`text-xs rounded-lg p-2 ${Math.abs(rateVsSuggested) < 5 ? 'bg-[#00A651]/10 text-[#00A651]' : rateVsSuggested < -5 ? 'bg-red-50 text-red-600' : 'bg-[#FFB800]/15 text-[#B37800]'}`}>
          {Math.abs(rateVsSuggested) < 5 ? <><Check className="w-3 h-3 inline mr-1" />Your rate is close to market ({rateVsSuggested.toFixed(1)}%)</> :
           rateVsSuggested < 0 ? <><TrendingDown className="w-3 h-3 inline mr-1" />Your rate is {Math.abs(rateVsSuggested).toFixed(0)}% below market — drivers may skip</> :
           <><TrendingUp className="w-3 h-3 inline mr-1" />Your rate is {rateVsSuggested.toFixed(0)}% above market — you'll get bookings fast</>}
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <p>{suggestion.explanation}</p>
      </div>

      <div className="flex gap-2">
        <Button onClick={apply} size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">Apply {formatINR(suggestion.suggested_rate)}</Button>
        <Button onClick={() => setSuggestion(null)} size="sm" variant="outline">Dismiss</Button>
      </div>
    </div>
  );
}
